/**
 * RaceScene.js - Main Racing Scene for Memex Racing
 * 
 * Handles all game phases: betting (30s) → race (5min) → results → auto-reset
 * Manages the core racing gameplay with random movement, power-ups, and win conditions.
 */

import Phaser from 'phaser';
import { GameEngine } from '../engine/GameEngine';
import { AuthenticatedScene } from './AuthenticatedScene';
import { addMemoryManagement } from '../systems/SceneCleanupUtils.js';
import { Player } from '../entities/Player';
import { TrackGenerator } from '../systems/TrackGenerator';
import { Obstacle, OBSTACLE_TYPES } from '../entities/Obstacle';
import { TemporaryBarrier, BARRIER_TYPES } from '../entities/TemporaryBarrier';

export class RaceScene extends AuthenticatedScene {
    constructor() {
        super({ key: 'RaceScene' });
        
        // Game state
        this.gamePhase = 'preparing'; // preparing, racing, results, resetting
        this.gameTime = 300; // 5 minutes in seconds
        this.raceNumber = 1;
        
        // Game objects
        this.players = [];
        this.boosters = [];
        this.skills = [];
        this.mToken = null;
        this.goalArrow = null;
        this.trackData = null;
        this.validPositions = [];
        this.usedPlayerSprites = new Set(); // Track used sprites to avoid duplicates
        
        // UI elements
        this.timerText = null;
        this.phaseText = null;
        this.autoChargePanel = null;
        // this.leaderboardPanel = null; // DISABLED per user request
        this.debugPanel = null;
        
        // Game data (player stats removed)
        this.gameData = {};
        this.rng = null;
        
        // Debug mode - temporarily enabled to verify alignment
        this.debugMode = true;
        this.debugGraphics = null;
        
        // Track parameters
        this.currentMap = 'classic';
        this.trackWidth = 1920;  // Match game resolution width
        this.trackHeight = 1080;  // Match game resolution height
        
        // Performance tracking
        this.lastBoosterSpawn = 0;
        this.lastSkillSpawn = 0;
    }

    init(data) {
        // Initialize basic game data (player data removed)
        this.gameData = data || {};
        console.log('[RaceScene] Initialized with game data:', this.gameData);
        
        // Initialize RNG with consistent seed for reproducible randomness
        this.rng = {
            seed: Date.now(),
            random: () => Math.random() // In real implementation, use Mersenne Twister
        };
    }

    create() {
        // Call parent create for authentication setup
        super.create();
        
        // Initialize game data with defaults (players removed)
        this.gameData = this.game.registry.get('gameData') || {};
        this.boosters = [];
        this.skills = [];
        this.obstacles = [];
        this.barriers = []; // Large temporary barriers
        
        // Initialize transition flags
        this.isLeavingScene = false;
        this.sceneReady = false;
        this.escPressed = false;  // ESC key debounce flag
        
        // Reset camera state
        if (this.cameras && this.cameras.main) {
            this.cameras.main.setAlpha(1);
            this.cameras.main.clearFX();
            // Force clear any stuck fade
            this.cameras.main.fadeEffect.alpha = 1;
        }
        
        // Add memory management to this scene
        addMemoryManagement(this);
        
        console.log('[RaceScene] Creating race scene with memory management');
        
        // Set background
        this.cameras.main.setBackgroundColor('#1a3a1a');
        
        // Create track
        this.createTrack();
        
        // Setup camera for better track visibility
        this.setupCamera();
        
        // Create debug graphics
        this.debugGraphics = this.add.graphics();
        this.debugGraphics.setDepth(1000);
        
        // Create UI
        this.createUI();
        
        // Setup input handlers
        this.setupInputHandlers();
        
        // Create game timers
        this.setupGameTimers();
        
        // Get music manager - it should already be playing from LoginScene
        const musicManager = this.game.registry.get('musicManager');
        if (musicManager && !musicManager.isPlaying) {
            console.log('[RaceScene] Resuming background music');
            musicManager.startMusic();
        }
        
        // Wait for assets to settle, then initialize game objects using managed timeout
        this.managedSetTimeout(() => {
            this.initializeGameObjects();
            this.showAutoChargeNotice();
            // Start race directly after 3 seconds notice
            this.managedSetTimeout(() => {
                this.startRacingPhase();
            }, 3000);
        }, 200);
        
        // Fade in from clear state
        if (this.cameras && this.cameras.main) {
            this.cameras.main.fadeFrom(500, 0, 0, 0);
        }
        
        // Mark scene as ready after fade in
        this.time.delayedCall(500, () => {
            this.sceneReady = true;
        });
        
        // Emit race scene ready event
        this.events.emit('RACE_SCENE_READY', this.gameData);
    }

    /**
     * Create the racing track
     */
    createTrack() {
        // Initialize track generator
        this.trackGenerator = new TrackGenerator(this.trackWidth, this.trackHeight);
        
        // Create graphics for the complete generated map
        const mapGraphics = this.add.graphics();
        mapGraphics.setDepth(100); // Above background image layer
        
        // Generate complete map with background and track
        const trackData = this.trackGenerator.generateCompleteMap(mapGraphics, this);
        console.log(`[RaceScene] Generated complete map with ${trackData.type} track, background: ${this.trackGenerator.selectedBackground}`);
        
        // Store valid positions from generator
        this.validPositions = trackData.validPositions;
        
        // Store safe area bounds for player movement
        this.safeMarginX = this.trackGenerator.marginX;
        this.safeMarginY = this.trackGenerator.marginY;
        this.safeMinX = this.trackGenerator.minX;
        this.safeMaxX = this.trackGenerator.maxX;
        this.safeMinY = this.trackGenerator.minY;
        this.safeMaxY = this.trackGenerator.maxY;
        
        // Use 100% full screen
        const scaleFactor = 1.0;
        const gameAreaWidth = this.trackWidth * scaleFactor;
        const gameAreaHeight = this.trackHeight * scaleFactor;
        const offsetX = 0;
        const offsetY = 0;
        const worldCenterX = offsetX + gameAreaWidth / 2;
        const worldCenterY = offsetY + gameAreaHeight / 2;
        
        // Create minimal UI background - only right panel for info
        const uiBg = this.add.graphics();
        uiBg.fillStyle(0x000000, 0.4);
        // REMOVED: No grey UI area on right side per user request
        
        // Add visual border around game area
        const border = this.add.graphics();
        border.lineStyle(3, 0x00ff00, 0.8);
        border.strokeRect(offsetX, offsetY, gameAreaWidth, gameAreaHeight);
        border.setDepth(50);
        
        // Debug visualization if enabled
        if (this.debugMode) {
            const debugGraphics = this.add.graphics();
            // Center cross
            debugGraphics.lineStyle(2, 0xff0000, 1);
            debugGraphics.moveTo(worldCenterX - 20, worldCenterY);
            debugGraphics.lineTo(worldCenterX + 20, worldCenterY);
            debugGraphics.moveTo(worldCenterX, worldCenterY - 20);
            debugGraphics.lineTo(worldCenterX, worldCenterY + 20);
            
            // Corner markers
            debugGraphics.fillStyle(0xffff00, 0.5);
            debugGraphics.fillCircle(offsetX, offsetY, 10);  // Top-left
            debugGraphics.fillCircle(offsetX + gameAreaWidth, offsetY, 10);  // Top-right
            debugGraphics.fillCircle(offsetX, offsetY + gameAreaHeight, 10);  // Bottom-left
            debugGraphics.fillCircle(offsetX + gameAreaWidth, offsetY + gameAreaHeight, 10);  // Bottom-right
            
            debugGraphics.setDepth(100);
            
            console.log('[RaceScene] Debug visualization enabled');
        }
        
        // Store consistent offsets for use in all position calculations
        this.gameAreaWidth = gameAreaWidth;
        this.gameAreaHeight = gameAreaHeight;
        this.gameOffsetX = offsetX;  // This is now centered
        this.gameOffsetY = offsetY;
        
        console.log(`[RaceScene] Track positioning - OffsetX: ${offsetX}, OffsetY: ${offsetY}, Center: (${worldCenterX}, ${worldCenterY})`);
        
        // Create collision data from the track template
        this.createTrackCollisionData();
    }

    
    /**
     * Create track collision data for pathfinding
     */
    createTrackCollisionData() {
        // Skip pixel-based collision - we're using generated track data
        console.log('[RaceScene] Using generated track data for collision detection');
        
        // Draw track visualization if in debug mode
        if (this.debugMode && this.debugGraphics) {
            this.trackGenerator.drawTrack(this.debugGraphics);
        }
        
        return; // Skip the rest of the pixel-based collision setup
    }
    
    /**
     * Regenerate map with new background for race variety
     */
    regenerateMap() {
        console.log('[RaceScene] Regenerating map for new race');
        
        // Initialize used backgrounds tracking if not exists
        if (!this.usedBackgrounds) {
            this.usedBackgrounds = new Set();
        }
        
        // Clear previous map graphics
        const existingMapGraphics = this.children.list.filter(child => 
            child.type === 'Graphics' && child.depth === 100
        );
        existingMapGraphics.forEach(graphics => graphics.destroy());
        
        // Clear previous background images  
        const existingBackgrounds = this.children.list.filter(child =>
            child.type === 'Image' && child.depth === 0
        );
        existingBackgrounds.forEach(bg => bg.destroy());
        
        // Create new track generator with background rotation
        this.trackGenerator = new TrackGenerator(this.trackWidth, this.trackHeight);
        
        // Implement background rotation - avoid recently used backgrounds
        const availableBackgrounds = this.trackGenerator.backgroundImages.filter(
            bg => !this.usedBackgrounds.has(bg)
        );
        
        // If all backgrounds used, reset the used set
        if (availableBackgrounds.length === 0) {
            console.log('[RaceScene] All backgrounds used, resetting rotation');
            this.usedBackgrounds.clear();
            // Ensure we don't use the exact same background as last race
            if (this.lastBackground) {
                this.usedBackgrounds.add(this.lastBackground);
            }
        }
        
        // Force selection from available backgrounds
        const finalAvailable = availableBackgrounds.length > 0 ? availableBackgrounds : 
            this.trackGenerator.backgroundImages.filter(bg => bg !== this.lastBackground);
            
        const selectedBg = finalAvailable[Math.floor(Math.random() * finalAvailable.length)];
        this.trackGenerator.selectedBackground = selectedBg;
        this.usedBackgrounds.add(selectedBg);
        this.lastBackground = selectedBg;
        
        console.log(`[RaceScene] Forced background selection: ${selectedBg} (${this.usedBackgrounds.size} used)`);
        
        // Create new map graphics
        const mapGraphics = this.add.graphics();
        mapGraphics.setDepth(100);
        
        // Generate new complete map
        const trackData = this.trackGenerator.generateCompleteMap(mapGraphics, this);
        console.log(`[RaceScene] Regenerated map with ${trackData.type} track, background: ${this.trackGenerator.selectedBackground}`);
        
        // Store valid positions from generator
        this.validPositions = trackData.validPositions;
        
        // Update track data
        this.trackData = trackData;
        
        console.log('[RaceScene] Map regeneration complete');
    }
    
    /**
     * Create fallback track data if template fails to load
     */
    createFallbackTrackData() {
        const canvas = document.createElement('canvas');
        canvas.width = this.trackWidth;
        canvas.height = this.trackHeight;
        const context = canvas.getContext('2d');
        
        // Create simple track layout
        context.fillStyle = '#000000';
        context.fillRect(0, 0, this.trackWidth, this.trackHeight);
        context.fillStyle = '#FFFFFF';
        
        // Simple rectangular track
        context.fillRect(100, 100, this.trackWidth - 200, this.trackHeight - 200);
        context.fillStyle = '#000000';
        context.fillRect(300, 300, this.trackWidth - 600, this.trackHeight - 600);
        
        this.trackData = context.getImageData(0, 0, this.trackWidth, this.trackHeight);
    }

    /**
     * Find valid positions on the track
     */
    findValidTrackPositions() {
        this.validPositions = [];
        
        // Use full screen dimensions (no scaling)
        const gameAreaWidth = this.trackWidth;
        const gameAreaHeight = this.trackHeight;
        const offsetX = 0;
        const offsetY = 0;
        
        // Sample every 20 pixels within the full screen area
        const startX = 40;
        const endX = gameAreaWidth - 40;
        const startY = 40;
        const endY = gameAreaHeight - 40;
        
        for (let x = startX; x < endX; x += 20) {
            for (let y = startY; y < endY; y += 20) {
                if (this.isPositionOnTrack(x, y)) {
                    this.validPositions.push({ x, y });
                }
            }
        }
        
        console.log(`[RaceScene] Found ${this.validPositions.length} valid track positions within scaled area`);
        console.log(`[RaceScene] Scaled bounds: (${startX}, ${startY}) to (${endX}, ${endY})`);
        
        // Debug: Show sample positions if found
        if (this.validPositions.length > 0) {
            console.log(`[RaceScene] Sample valid positions:`, this.validPositions.slice(0, 3));
        } else {
            console.error(`[RaceScene] CRITICAL: NO VALID POSITIONS FOUND! Track detection may be broken.`);
            // Create a grid of fallback positions within the scaled area
            const margin = 100;
            const spacing = 100;
            
            for (let x = startX + margin; x < endX - margin; x += spacing) {
                for (let y = startY + margin; y < endY - margin; y += spacing) {
                    this.validPositions.push({ x, y });
                }
            }
            
            console.log(`[RaceScene] Added ${this.validPositions.length} fallback positions in grid pattern`);
        }
    }

    /**
     * Check if position is on track
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     * @returns {boolean} Is on track
     */
    isPositionOnTrack(x, y) {
        // Use track generator's validation if available
        if (this.trackGenerator) {
            return this.trackGenerator.isPositionValid(x, y);
        }
        
        // Fallback to simple boundary check
        const margin = 100;
        return x >= margin && x < this.trackWidth - margin && 
               y >= margin && y < this.trackHeight - margin;
    }

    /**
     * Create UI elements
     */
    createUI() {
        // Use track-based coordinates matching camera center
        const timerX = this.trackWidth / 2;  // Match camera center horizontally
        const timerY = 150;   // Move down to 150px to ensure full visibility
        
        // FIRST: Create background box for timer
        const timerBg = this.add.rectangle(timerX, timerY, 280, 70, 0x000000, 0.85);
        timerBg.setDepth(99999); // High depth but below text
        timerBg.setScrollFactor(0);
        timerBg.setStrokeStyle(4, 0xffff00); // Thick yellow border
        timerBg.setVisible(true);
        
        // SECOND: Create timer text on top of background
        this.timerText = this.add.text(timerX, timerY, 'TIME: 5:00', {
            fontSize: '48px',  // Slightly smaller to fit in box
            fontFamily: 'Arial Black',
            color: '#ffff00',  // Yellow for visibility
            fontWeight: 'bold',
            stroke: '#000000',  // Black stroke for contrast
            strokeThickness: 4,
            shadow: {
                offsetX: 2,
                offsetY: 2,
                color: '#000000',
                blur: 4,
                stroke: true,
                fill: true
            }
        }).setOrigin(0.5);
        this.timerText.setDepth(100000); // Higher than background
        this.timerText.setScrollFactor(0); // Keep UI fixed on screen
        this.timerText.setPosition(timerX, timerY); // Ensure position is set
        this.timerText.setVisible(true); // Explicitly set visible
        this.timerText.setActive(true); // Explicitly set active
        this.timerText.setAlpha(1); // Full opacity
        
        // Debug logging
        console.log(`[RaceScene] Timer box and text created at position (${timerX}, ${timerY})`);
        
        // Phase indicator - moved to right side panel
        this.phaseText = this.add.text(1824, 80, 'PREPARING...', {
            fontSize: '16px',
            fontFamily: 'Courier New',
            color: '#ffff00',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5);
        this.phaseText.setDepth(1000);
        this.phaseText.setScrollFactor(0); // Keep UI fixed on screen
        
        // Log UI creation for debugging
        console.log('[RaceScene] UI elements created:', {
            timerText: !!this.timerText,
            phaseText: !!this.phaseText,
            timerPos: this.timerText ? { x: this.timerText.x, y: this.timerText.y } : null,
            phasePos: this.phaseText ? { x: this.phaseText.x, y: this.phaseText.y } : null
        });
        
        // Race number - moved to right panel
        this.add.text(1824, 20, `Race #${this.raceNumber}`, {
            fontSize: '16px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5).setDepth(1000);
        
        // Create auto-charge notification panel
        this.createAutoChargePanel();
        
        // Create leaderboard panel - DISABLED per user request
        // this.createLeaderboardPanel();
        
        // Create debug panel (initially hidden)
        this.createDebugPanel();
        
        // Create player points panel
        this.createPlayerPointsPanel();
        
        // Instructions removed per user request
    }

    /**
     * Create auto-charge notification panel
     */
    createAutoChargePanel() {
        // Position in right margin area
        const panelX = 1824;  // Right margin center
        const panelY = 300;
        
        // Panel background - transparent (30% opacity)
        const panelBg = this.add.graphics();
        panelBg.fillStyle(0x000000, 0.3);
        panelBg.fillRoundedRect(panelX - 85, panelY - 50, 170, 200, 10);
        // Removed green border lines for cleaner UI
        
        // Panel title
        const panelTitle = this.add.text(panelX, panelY - 20, 'RACE INFO', {
            fontSize: '24px',
            fontFamily: 'Courier New',
            color: '#00ff00',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        
        // Auto-charge notice
        this.autoChargeText = this.add.text(panelX, panelY + 20, 'Auto-Bet Active', {
            fontSize: '16px',
            fontFamily: 'Courier New',
            color: '#ffff00',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        
        
        this.add.text(panelX, panelY + 45, '100 tokens', {
            fontSize: '18px',
            fontFamily: 'Courier New',
            color: '#ffffff'
        }).setOrigin(0.5);
        
        this.add.text(panelX, panelY + 70, 'on yourself', {
            fontSize: '14px',
            fontFamily: 'Courier New',
            color: '#ffffff'
        }).setOrigin(0.5);
        
        // Winning info
        this.add.text(panelX, panelY + 100, 'Win: 500 tokens', {
            fontSize: '14px',
            fontFamily: 'Courier New',
            color: '#00ff00'
        }).setOrigin(0.5);
        
        this.add.text(panelX, panelY + 120, 'Find the M token!', {
            fontSize: '14px',
            fontFamily: 'Courier New',
            color: '#ffffff'
        }).setOrigin(0.5);
        
        this.autoChargePanel = {
            background: panelBg,
            title: panelTitle,
            chargeText: this.autoChargeText
        };
    }

    /**
     * Create transparent player points panel
     */
    createPlayerPointsPanel() {
        // Position on left side of screen (opposite of auto-charge panel)
        const panelX = 160;  // Left margin center
        const panelY = 300;
        const panelWidth = 280;
        const panelHeight = 250;
        
        // Panel background - more transparent (20% opacity)
        const panelBg = this.add.graphics();
        panelBg.fillStyle(0x000000, 0.2);
        panelBg.fillRoundedRect(panelX - panelWidth/2, panelY - panelHeight/2, panelWidth, panelHeight, 15);
        panelBg.lineStyle(2, 0x00ff00, 0.5);
        panelBg.strokeRoundedRect(panelX - panelWidth/2, panelY - panelHeight/2, panelWidth, panelHeight, 15);
        panelBg.setDepth(1000);
        panelBg.setScrollFactor(0); // Keep UI fixed on screen
        
        // Panel title
        const panelTitle = this.add.text(panelX, panelY - 100, 'PLAYER SCORES', {
            fontSize: '20px',
            fontFamily: 'Courier New',
            color: '#00ff00',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        panelTitle.setDepth(1001);
        panelTitle.setScrollFactor(0);
        
        // Create player score texts array
        this.playerScoreTexts = [];
        
        // Create score displays for each player (up to 6 players)
        for (let i = 0; i < 6; i++) {
            const playerScoreText = this.add.text(panelX, panelY - 60 + (i * 25), `P${i + 1}: 0 points`, {
                fontSize: '16px',
                fontFamily: 'Courier New',
                color: i === 0 ? '#ffff00' : '#ffffff', // Highlight current player (P1) in yellow
                fontWeight: i === 0 ? 'bold' : 'normal'
            }).setOrigin(0.5);
            
            playerScoreText.setDepth(1001);
            playerScoreText.setScrollFactor(0);
            playerScoreText.setVisible(i < this.players.length); // Only show if player exists
            
            this.playerScoreTexts.push(playerScoreText);
        }
        
        // Store panel elements for updates and cleanup
        this.playerPointsPanel = {
            background: panelBg,
            title: panelTitle,
            scoreTexts: this.playerScoreTexts
        };
        
        console.log('[RaceScene] Player points panel created');
    }

    /**
     * Add points to a player and update display
     * @param {number} playerIndex - Index of the player (0-5)
     * @param {number} points - Points to add
     */
    addPlayerPoints(playerIndex, points) {
        if (playerIndex < 0 || playerIndex >= this.playerStats.length) return;
        
        this.playerStats[playerIndex].totalScore += points;
        this.updatePlayerPointsPanel();
        
        console.log(`[RaceScene] Added ${points} points to Player ${playerIndex + 1}. Total: ${this.playerStats[playerIndex].totalScore}`);
    }

    /**
     * Update the player points panel with current scores
     */
    updatePlayerPointsPanel() {
        if (!this.playerScoreTexts || !this.playerStats) return;
        
        for (let i = 0; i < this.playerScoreTexts.length; i++) {
            if (i < this.playerStats.length && i < this.players.length) {
                const playerName = this.playerStats[i].name || `Player ${i + 1}`;
                const playerScore = this.playerStats[i].totalScore || 0;
                
                this.playerScoreTexts[i].setText(`${playerName}: ${playerScore} pts`);
                this.playerScoreTexts[i].setVisible(true);
            } else {
                this.playerScoreTexts[i].setVisible(false);
            }
        }
    }

    /**
     * Setup camera for proper track display
     */
    setupCamera() {
        // Use full screen with no margins for complete visibility
        const gameAreaWidth = this.trackWidth;
        const gameAreaHeight = this.trackHeight;
        const offsetX = 0;
        const offsetY = 0;
        
        // Set world bounds for physics to match full screen
        this.physics.world.setBounds(offsetX, offsetY, gameAreaWidth, gameAreaHeight);
        
        // Set camera bounds to full screen
        this.cameras.main.setBounds(0, 0, this.trackWidth, this.trackHeight);
        
        // Start with camera centered on full screen
        this.cameras.main.centerOn(this.trackWidth / 2, this.trackHeight / 2);
        
        // Set zoom to 1.0 to show game at full size
        this.cameras.main.setZoom(1.0);  // No zoom - show at actual size
        
        // Make sure camera is visible
        this.cameras.main.setVisible(true);
        this.cameras.main.setAlpha(1);
        
        console.log(`[RaceScene] Camera setup complete (full screen):`);
        console.log(`  - Dimensions: ${this.trackWidth}x${this.trackHeight}`);
        console.log(`  - Game area: ${gameAreaWidth}x${gameAreaHeight}`);
        console.log(`  - Physics bounds: (${offsetX}, ${offsetY}) to (${offsetX + gameAreaWidth}, ${offsetY + gameAreaHeight})`);
        console.log(`  - Camera bounds: (0, 0) to (${this.trackWidth}, ${this.trackHeight})`);
        console.log(`  - Camera center: (${this.cameras.main.centerX}, ${this.cameras.main.centerY})`);
        console.log(`  - Camera zoom: ${this.cameras.main.zoom}`);
        console.log(`  - Full screen coverage with no margins`);
    }
    
    // REMOVED: createLeaderboardPanel() - Disabled per user request

    /**
     * Create debug panel - Enhanced for visual testing
     */
    createDebugPanel() {
        const panelX = 1700;  // Move debug panel to right side to avoid overlap
        const panelY = 600;   // Lower position for debug panel
        
        // Larger panel background for more info
        const panelBg = this.add.graphics();
        panelBg.fillStyle(0x000000, 0.95);
        panelBg.fillRoundedRect(panelX - 200, panelY - 80, 400, 380, 10);
        panelBg.lineStyle(3, 0x00ff00);
        panelBg.strokeRoundedRect(panelX - 200, panelY - 80, 400, 380, 10);
        panelBg.setDepth(999);
        
        // Panel title
        const panelTitle = this.add.text(panelX, panelY - 50, '🎮 VISUAL DEBUG OVERLAY', {
            fontSize: '18px',
            fontFamily: 'Courier New',
            color: '#00ff00',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        panelTitle.setDepth(1000);
        
        // Debug content - main info
        this.debugText = this.add.text(panelX - 180, panelY - 20, '', {
            fontSize: '11px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            lineSpacing: 2
        }).setOrigin(0, 0);
        this.debugText.setDepth(1000);
        
        // Player details section
        this.playerDebugText = this.add.text(panelX - 180, panelY + 120, '', {
            fontSize: '10px',
            fontFamily: 'Courier New',
            color: '#ffff00',
            lineSpacing: 1
        }).setOrigin(0, 0);
        this.playerDebugText.setDepth(1000);
        
        // Background info section
        this.bgDebugText = this.add.text(panelX - 180, panelY + 230, '', {
            fontSize: '10px',
            fontFamily: 'Courier New',
            color: '#00ffff',
            lineSpacing: 1
        }).setOrigin(0, 0);
        this.bgDebugText.setDepth(1000);
        
        this.debugPanel = {
            background: panelBg,
            title: panelTitle,
            contentText: this.debugText,
            playerText: this.playerDebugText,
            bgText: this.bgDebugText
        };
        
        // Initially hidden
        this.setDebugPanelVisible(false);
    }

    /**
     * Setup input handlers
     */
    setupInputHandlers() {
        // Debug mode toggle - using managed event listener
        this.managedAddKeyboardHandler('keydown-D', () => {
            this.debugMode = !this.debugMode;
            this.setDebugPanelVisible(this.debugMode);
            console.log(`[RaceScene] Debug mode: ${this.debugMode ? 'ON' : 'OFF'}`);
        });
        
        // ESC to return to lobby/menu - using managed event listener with proper debouncing
        this.escPressed = false;
        this.managedAddKeyboardHandler('keydown-ESC', () => {
            if (this.sceneReady && !this.isLeavingScene && !this.escPressed) {
                this.escPressed = true;
                this.returnToLobby();
                
                // Reset ESC debounce after transition completes
                this.time.delayedCall(1000, () => {
                    this.escPressed = false;
                });
            }
        });
        
        // Number keys disabled - no betting shortcuts needed
        
        // Setup player customization event listeners
        this.setupPlayerCustomizationListeners();
    }
    
    /**
     * Setup listeners for player customization from UI
     */
    setupPlayerCustomizationListeners() {
        // Listen for player updates from UI
        this.events.on('playerUpdate', (data) => {
            this.handlePlayerUpdate(data);
        });
        
        // Listen for player join events
        this.events.on('playerJoined', (data) => {
            this.handlePlayerJoined(data);
        });
    }
    
    /**
     * Handle player update from UI (name or image)
     */
    handlePlayerUpdate(data) {
        const { playerId, type, data: updateData } = data;
        
        if (!this.players || !this.players[playerId]) {
            console.warn(`[RaceScene] Player ${playerId} not found for update`);
            return;
        }
        
        const player = this.players[playerId];
        
        if (type === 'name') {
            // Update player name
            player.name = updateData;
            if (player.nameText) {
                player.nameText.setText(updateData);
            }
            console.log(`[RaceScene] Updated player ${playerId} name to: ${updateData}`);
            
        } else if (type === 'image' && updateData instanceof HTMLCanvasElement) {
            // Apply custom texture from canvas
            const textureKey = `custom_player_${playerId}`;
            
            // Remove old texture if exists
            if (this.textures.exists(textureKey)) {
                this.textures.remove(textureKey);
            }
            
            // Add new texture from canvas
            this.textures.addCanvas(textureKey, updateData);
            
            // Apply to player
            player.applyCustomTexture(textureKey);
            
            console.log(`[RaceScene] Applied custom texture to player ${playerId}`);
        }
    }
    
    /**
     * Handle player joined event from UI
     */
    handlePlayerJoined(data) {
        const { playerId, playerName, customImage, betAmount } = data;
        
        console.log(`[RaceScene] Player ${playerId} joined: ${playerName} with ${betAmount} coin bet`);
        
        // Update player stats if they exist
        if (this.playerStats && this.playerStats[playerId]) {
            this.playerStats[playerId].name = playerName;
            this.playerStats[playerId].hasJoined = true;
        }
        
        // Apply custom image if provided
        if (customImage && this.players && this.players[playerId]) {
            this.handlePlayerUpdate({
                playerId: playerId,
                type: 'image',
                data: customImage
            });
        }
    }

    /**
     * Setup game timers
     */
    setupGameTimers() {
        // Main game timer (1 second intervals) - using managed timer
        this.managedAddTimerEvent({
            delay: 1000,
            callback: this.updateGameTimer,
            callbackScope: this,
            loop: true
        });
        
        // Dynamic spawn system - rates increase based on timer
        this.lastBoosterSpawn = 0;
        this.lastSkillSpawn = 0;
        this.lastObstacleSpawn = 0;
        this.lastBarrierSpawn = 0;
        this.baseBoosterInterval = 3000; // 3 seconds
        this.baseSkillInterval = 4000;   // 4 seconds (doubled spawn rate from 8s)
        this.baseObstacleInterval = 5000; // 5 seconds base spawn rate
        this.baseBarrierInterval = 15000; // 15 seconds - RARE large barriers
        
        console.log('[RaceScene] Dynamic spawn system initialized - rates will increase at 2min (2x) and 1min (4x)');
    }

    /**
     * Initialize game objects
     */
    initializeGameObjects() {
        console.log('[RaceScene] Initializing game objects');
        
        // Reset used sprites for new race
        this.usedPlayerSprites.clear();
        
        // Initialize player stats
        this.initializePlayerStats();
        
        // Spawn players at random position
        this.spawnPlayers();
        
        // Spawn M token far from players
        this.spawnMToken();
        
        console.log('[RaceScene] Game objects initialized');
    }

    /**
     * Initialize player statistics
     */
    initializePlayerStats() {
        this.playerStats = [];
        for (let i = 0; i < 6; i++) {
            this.playerStats.push({
                index: i,
                name: `Player ${i + 1}`,
                wins: 0,
                totalScore: 0,
                races: 0,
                earnings: 0
            });
        }
    }
    
    /**
     * Spawn all players at a random valid position
     */
    spawnPlayers() {
        console.log('[RaceScene] Spawning players');
        
        // Get start positions from track generator
        let startPositions;
        if (this.trackGenerator) {
            startPositions = this.trackGenerator.getStartPositions(6);
        } else {
            // Fallback to finding a valid starting position
            const startPos = this.findValidStartPosition();
            if (!startPos) {
                console.error('[RaceScene] Could not find valid start position');
                return;
            }
            // Create positions around the start position
            startPositions = [];
            for (let i = 0; i < 6; i++) {
                const angle = (Math.PI * 2 * i) / 6;
                startPositions.push({
                    x: startPos.x + Math.cos(angle) * 50,
                    y: startPos.y + Math.sin(angle) * 50
                });
            }
        }
        
        // Create 6 players at their designated positions
        for (let i = 0; i < 6; i++) {
            const player = this.createPlayer(i, startPositions[i]);
            this.players.push(player);
        }
        
        console.log(`[RaceScene] Spawned ${this.players.length} players using generated track positions`);
    }
    
    /**
     * Create a single player
     */
    createPlayer(index, position) {
        // Use the position directly from track generator
        const playerPos = {
            x: position.x,
            y: position.y
        };
        
        // Create player sprite texture key - custom for human or random for AI
        let playerSpriteKey;
        if (index === 0) {
            // Check for custom player image from lobby
            const customImage = this.registry.get('customPlayerImage');
            if (customImage && this.textures.exists(customImage)) {
                playerSpriteKey = customImage;
                console.log(`[RaceScene] Using custom player image: ${customImage}`);
            } else {
                // Default human player sprite
                playerSpriteKey = 'play_pug1';
            }
            this.usedPlayerSprites.add(playerSpriteKey);
        } else {
            // AI players get unique sprites from available pool (no duplicates)
            const availableSprites = [];
            for (let i = 1; i <= 20; i++) {
                const spriteKey = `play_pug${i}`;
                if (!this.usedPlayerSprites.has(spriteKey) && this.textures.exists(spriteKey)) {
                    availableSprites.push(spriteKey);
                }
            }
            
            // If we have available sprites, pick one randomly
            if (availableSprites.length > 0) {
                const randomIndex = Math.floor(Math.random() * availableSprites.length);
                playerSpriteKey = availableSprites[randomIndex];
                this.usedPlayerSprites.add(playerSpriteKey);
            } else {
                // Fallback if all sprites are used (shouldn't happen with 20 sprites and 6 players)
                playerSpriteKey = `play_pug${index + 1}`;
                console.warn(`[RaceScene] All sprites used, falling back to ${playerSpriteKey}`);
            }
        }
        const playerName = this.playerStats[index]?.name || `Player ${index + 1}`;
        
        console.log(`[RaceScene] Creating player ${index} (${playerName}) at (${playerPos.x}, ${playerPos.y})`);
        
        // Create player using Player class
        const player = new Player(this, playerPos.x, playerPos.y, playerSpriteKey, null, {
            index: index,
            name: playerName,
            isAI: index > 0,  // First player is human, rest are AI
            isCurrentPlayer: index === 0
        });
        
        // Set initial properties
        player.setDepth(200 + (index * 10));
        player.setVisible(true);
        player.setActive(true);
        
        return player;
    }
    
    /**
     * Spawn M token at farthest position from players
     */
    spawnMToken() {
        if (this.players.length === 0) {
            console.warn('[RaceScene] No players to spawn M token from');
            return;
        }
        
        // Clear existing M token
        if (this.mToken) {
            this.mToken.destroy();
            this.mToken = null;
        }
        
        // Clear existing goal arrow
        if (this.goalArrow) {
            this.goalArrow.destroy();
            this.goalArrow = null;
        }
        
        // Calculate average player position
        let avgX = 0, avgY = 0;
        this.players.forEach(p => {
            avgX += p.x;
            avgY += p.y;
        });
        avgX /= this.players.length;
        avgY /= this.players.length;
        
        // Place token far from average player position
        const margin = 200; // Keep away from edges
        let tokenPos;
        
        // Simple approach: place on opposite quadrant from players
        if (avgX < this.trackWidth / 2) {
            // Players on left, place token on right
            tokenPos = {
                x: this.trackWidth - margin - Math.random() * 300,
                y: avgY < this.trackHeight / 2 ? 
                    this.trackHeight - margin - Math.random() * 300 : 
                    margin + Math.random() * 300
            };
        } else {
            // Players on right, place token on left
            tokenPos = {
                x: margin + Math.random() * 300,
                y: avgY < this.trackHeight / 2 ? 
                    this.trackHeight - margin - Math.random() * 300 : 
                    margin + Math.random() * 300
            };
        }
        
        // Ensure within bounds
        tokenPos.x = Math.max(margin, Math.min(this.trackWidth - margin, tokenPos.x));
        tokenPos.y = Math.max(margin, Math.min(this.trackHeight - margin, tokenPos.y));
        
        console.log(`[RaceScene] M token positioned at (${tokenPos.x.toFixed(0)}, ${tokenPos.y.toFixed(0)}) away from players at (${avgX.toFixed(0)}, ${avgY.toFixed(0)})`);
        
        // Create M token with fallback texture
        let tokenTexture = 'mtoken';
        
        // Check for existing textures or create fallback
        if (this.textures.exists('mtoken_real')) {
            tokenTexture = 'mtoken_real';
        } else if (!this.textures.exists('mtoken')) {
            // Create fallback M token texture
            const graphics = this.add.graphics();
            graphics.fillStyle(0xffff00, 1);
            graphics.fillCircle(32, 32, 30);
            graphics.fillStyle(0x000000, 1);
            graphics.setDefaultStyles({ fontSize: '40px', fontFamily: 'Arial Black' });
            graphics.generateTexture('mtoken', 64, 64);
            graphics.destroy();
            
            // Add M text on token
            const tempText = this.add.text(32, 32, 'M', {
                fontSize: '32px',
                fontFamily: 'Arial Black',
                color: '#000000',
                fontWeight: 'bold'
            }).setOrigin(0.5);
            
            const rt = this.add.renderTexture(0, 0, 64, 64);
            const tempGraphics = this.add.graphics();
            tempGraphics.fillStyle(0xffff00, 1);
            tempGraphics.fillCircle(32, 32, 30);
            rt.draw(tempGraphics);
            rt.draw(tempText, 32, 32);
            rt.saveTexture('mtoken');
            
            tempGraphics.destroy();
            tempText.destroy();
            rt.destroy();
            
            console.log('[RaceScene] Created fallback M token texture');
        }
        
        this.mToken = this.physics.add.sprite(tokenPos.x, tokenPos.y, tokenTexture);
        this.mToken.setScale(0.6); // Increased from 0.4 to 0.6 for better visibility
        this.mToken.setDepth(500); // Higher depth to appear above everything
        this.mToken.setActive(true); // Ensure it's active
        this.mToken.setVisible(true); // Ensure it's visible
        
        // Add glow effect to make it more visible
        this.mToken.setTint(0xffff00); // Yellow tint
        
        // Add goal indicator - bigger and more visible
        this.goalArrow = this.add.text(tokenPos.x, tokenPos.y - 50, '⬇ M ⬇', {
            fontSize: '40px',
            color: '#ffff00',
            stroke: '#000000',
            strokeThickness: 3
        }).setOrigin(0.5);
        this.goalArrow.setDepth(501); // Above token
        
        this.managedCreateTween({
            targets: this.goalArrow,
            y: tokenPos.y - 50,
            duration: 1000,
            yoyo: true,
            repeat: -1
        });
        
        console.log('[RaceScene] M token spawned at', tokenPos, 'Valid:', this.isPositionOnTrack(tokenPos.x, tokenPos.y));
        console.log('[RaceScene] M token properties:', {
            x: this.mToken.x,
            y: this.mToken.y,
            visible: this.mToken.visible,
            active: this.mToken.active,
            depth: this.mToken.depth,
            scale: this.mToken.scaleX
        });
    }
    
    /**
     * Create M token at specific position (fallback method)
     */
    createMTokenAtPosition(x, y) {
        console.log(`[RaceScene] Creating M token at fallback position (${x}, ${y})`);
        
        // Create M token texture if needed
        let tokenTexture = 'mtoken';
        if (!this.textures.exists('mtoken')) {
            // Create fallback M token texture
            const graphics = this.add.graphics();
            graphics.fillStyle(0xffff00, 1);
            graphics.fillCircle(32, 32, 30);
            graphics.fillStyle(0x000000, 1);
            
            const rt = this.add.renderTexture(0, 0, 64, 64);
            rt.draw(graphics);
            
            const tempText = this.add.text(32, 32, 'M', {
                fontSize: '32px',
                fontFamily: 'Arial Black',
                color: '#000000'
            }).setOrigin(0.5);
            rt.draw(tempText);
            
            rt.saveTexture('mtoken');
            graphics.destroy();
            tempText.destroy();
            rt.destroy();
        }
        
        this.mToken = this.physics.add.sprite(x, y, tokenTexture);
        this.mToken.setScale(0.6); // Increased for better visibility
        this.mToken.setDepth(500); // Higher depth to appear above everything
        this.mToken.setActive(true);
        this.mToken.setVisible(true);
        this.mToken.setTint(0xffff00); // Yellow tint for visibility
        
        // Add goal indicator - bigger and more visible
        this.goalArrow = this.add.text(x, y - 50, '⬇ M ⬇', {
            fontSize: '40px',
            color: '#ffff00',
            stroke: '#000000',
            strokeThickness: 3
        }).setOrigin(0.5);
        this.goalArrow.setDepth(501); // Above token
        
        this.managedCreateTween({
            targets: this.goalArrow,
            y: y - 50,
            duration: 1000,
            yoyo: true,
            repeat: -1
        });
    }
    
    /**
     * Find a valid starting position with enough space for all players
     */
    findValidStartPosition() {
        const attempts = 100;
        
        for (let i = 0; i < attempts; i++) {
            const pos = this.validPositions[Math.floor(this.rng.random() * this.validPositions.length)];
            
            if (pos && this.checkSpaceForPlayers(pos, 6)) {
                return pos;
            }
        }
        
        // Fallback to center if no valid position found
        return { x: this.trackWidth / 2, y: this.trackHeight / 2 };
    }

    /**
     * Show auto-charge notice
     */
    showAutoChargeNotice() {
        console.log('[RaceScene] Showing auto-charge notice');
        
        // Show notification message
        const noticeText = this.add.text(960, 400, 'AUTO-BET: 100 TOKENS ON YOURSELF', {
            fontSize: '36px',
            fontFamily: 'Courier New',
            color: '#ffff00',
            fontWeight: 'bold',
            stroke: '#000000',
            strokeThickness: 4
        }).setOrigin(0.5);
        
        // Pulse animation
        this.tweens.add({
            targets: noticeText,
            scaleX: 1.2,
            scaleY: 1.2,
            duration: 500,
            yoyo: true,
            repeat: 2,
            onComplete: () => {
                noticeText.destroy();
            }
        });
    }

    /**
     * Start racing phase
     */
    startRacingPhase() {
        console.log('[RaceScene] Starting racing phase');
        
        this.gamePhase = 'racing';
        this.gameTime = 300; // 5 minutes
        
        this.phaseText.setText('RACE IN PROGRESS');
        this.phaseText.setColor('#00ff00');
        
        if (this.autoChargeText) {
            this.autoChargeText.setText('Race in progress!');
        }
        
        // Keep camera fixed to show full game world
        // Don't follow player to maintain full view of the track
        this.cameras.main.stopFollow();
        this.cameras.main.centerOn(this.trackWidth / 2, this.trackHeight / 2);
        this.cameras.main.setZoom(1.0);  // Keep at full size
        console.log('[RaceScene] Camera fixed at center, showing full track');
        
        // Show GO! message
        const goText = this.add.text(960, 540, 'GO!', {
            fontSize: '128px',
            fontFamily: 'Courier New',
            color: '#00ff00',
            stroke: '#000000',
            strokeThickness: 8
        }).setOrigin(0.5);
        
        this.managedCreateTween({
            targets: goText,
            scale: 1.5,
            alpha: 0,
            duration: 1000,
            onComplete: () => goText.destroy()
        });
        
            // Activate all players for racing
        this.players.forEach((player, index) => {
            player.active = true;
            player.stuckCounter = 0;
            player.moveDirection = this.rng.random() * Math.PI * 2;
            
            console.log(`[RaceScene] Activating player ${index}: ${player.name}`);
        });
        
        // Spawn initial boosters and skills
        console.log('[RaceScene] Spawning initial boosters and skills');
        for (let i = 0; i < 3; i++) {
            this.maybeSpawnBooster(1); // Normal rate initially
        }
        for (let i = 0; i < 2; i++) {
            this.maybeSpawnSkill(1); // Normal rate initially
        }
        
        // Update player points panel to show initial scores
        this.updatePlayerPointsPanel();
    }

    /**
     * End race with results
     */
    endRace(winner = null) {
        console.log('[RaceScene] Ending race', winner ? `Winner: Player ${winner.index + 1}` : 'Time up');
        
        this.gamePhase = 'results';
        
        // Stop all player movement
        this.players.forEach(player => {
            player.active = false;
            if (player.body) {
                player.body.setVelocity(0, 0);
            }
        });
        
        // Show results
        this.showRaceResults(winner);
        
        // Update statistics
        this.updatePlayerStatistics(winner);
        
        // Show play again dialog after 3 seconds
        this.managedSetTimeout(() => {
            this.showPlayAgainDialog();
        }, 3000);
        
        // Emit race finished event
        this.events.emit('RACE_FINISHED', {
            winner: winner ? winner.index : null,
            raceNumber: this.raceNumber,
            gameData: this.gameData
        });
    }

    /**
     * Update game state every frame
     */
    update(time, delta) {
        // Allow movement in all phases except results
        if (this.gamePhase === 'results' || this.players.length === 0) {
            return;
        }
        
        // Clear debug graphics
        if (this.debugGraphics) {
            this.debugGraphics.clear();
        }
        
        // Update all players with blind horse movement
        this.updatePlayers(time, delta);
        
        // Update obstacles
        this.obstacles.forEach(obstacle => {
            if (obstacle && obstacle.active) {
                obstacle.update(time, delta);
            }
        });
        
        // Update LARGE barriers
        this.barriers.forEach(barrier => {
            if (barrier && barrier.active) {
                barrier.update(time, delta);
            }
        });
        
        // Clean up destroyed obstacles
        this.obstacles = this.obstacles.filter(o => o && o.active);
        
        // Clean up destroyed barriers
        this.barriers = this.barriers.filter(b => b && b.active);
        
        // Add survival points every 5 seconds
        if (!this.lastSurvivalPointsTime) this.lastSurvivalPointsTime = time;
        if (time - this.lastSurvivalPointsTime >= 5000) {
            this.players.forEach((player, index) => {
                if (player && player.active) {
                    this.addPlayerPoints(index, 1); // 1 point for surviving 5 seconds
                }
            });
            this.lastSurvivalPointsTime = time;
        }
        
        // Check collisions
        this.checkCollisions();
        
        // Update debug info
        if (this.debugMode) {
            this.updateDebugInfo();
        }
        
        // Check for stuck players and spawn items
        this.updateGameSystems(time);
        
        // Dynamic booster and skill spawning based on timer
        this.updateDynamicSpawning(time);
    }

    /**
     * Update all players with blind horse AI movement
     */
    updatePlayers(time, delta) {
        this.players.forEach((player, index) => {
            if (!player || !player.active) return;
            
            // Update player movement using built-in blind horse AI
            player.updateMovement(delta);
            
            // REMOVED: No more track validation - players move freely within boundaries
            
            // Check for stuck players regardless of whether they're on/off track
            if (player.stuckCounter > 30) {
                this.handleStuckPlayer(player);
            }
            
            // Debug visualization for current player
            if (this.debugMode && index === 0) {
                // Draw movement direction
                this.debugGraphics.lineStyle(2, 0x00ff00);
                this.debugGraphics.beginPath();
                this.debugGraphics.moveTo(player.x, player.y);
                this.debugGraphics.lineTo(
                    player.x + Math.cos(player.moveDirection) * 30,
                    player.y + Math.sin(player.moveDirection) * 30
                );
                this.debugGraphics.strokePath();
                
                // Show stuck counter if high
                if (player.stuckCounter > 10) {
                    const stuckText = this.add.text(
                        player.x, 
                        player.y - 30, 
                        `Stuck: ${player.stuckCounter}`,
                        {
                            fontSize: '12px',
                            color: player.stuckCounter > 60 ? '#ff0000' : '#ffff00',
                            backgroundColor: '#000000'
                        }
                    ).setOrigin(0.5);
                    
                    this.time.delayedCall(100, () => stuckText.destroy());
                }
            }
        });
    }
    
    /**
     * Check all collision types
     */
    checkCollisions() {
        // Token collision (win condition)
        if (this.mToken) {
            this.physics.overlap(this.players, this.mToken, this.handleTokenCollision, null, this);
        }
        
        // Booster collisions
        if (this.boosters.length > 0) {
            this.physics.overlap(this.players, this.boosters, this.handleBoosterCollision, null, this);
        }
        
        // Skill collisions
        if (this.skills.length > 0) {
            this.physics.overlap(this.players, this.skills, this.handleSkillCollision, null, this);
        }
        
        // Obstacle collisions
        if (this.obstacles.length > 0) {
            this.physics.overlap(this.players, this.obstacles, this.handleObstacleCollision, null, this);
        }
        
        // LARGE barrier collisions - dramatic impacts!
        if (this.barriers.length > 0) {
            this.physics.overlap(this.players, this.barriers, this.handleBarrierCollision, null, this);
        }
        
        // Player-to-player collisions
        this.checkPlayerCollisions();
    }
    
    /**
     * Check player-to-player collisions
     */
    checkPlayerCollisions() {
        for (let i = 0; i < this.players.length; i++) {
            for (let j = i + 1; j < this.players.length; j++) {
                const player1 = this.players[i];
                const player2 = this.players[j];
                
                if (player1 && player2 && player1.active && player2.active) {
                    const distance = Phaser.Math.Distance.Between(
                        player1.x, player1.y, player2.x, player2.y
                    );
                    
                    if (distance < 30) {
                        this.handlePlayerCollision(player1, player2);
                    }
                }
            }
        }
    }
    
    /**
     * Handle collision between two players
     */
    handlePlayerCollision(player1, player2) {
        // Bubble protection bounces other player away
        if (player1.hasBubble) {
            const angle = Phaser.Math.Angle.Between(player1.x, player1.y, player2.x, player2.y);
            player2.x += Math.cos(angle) * 25;
            player2.y += Math.sin(angle) * 25;
        }
        
        if (player2.hasBubble) {
            const angle = Phaser.Math.Angle.Between(player2.x, player2.y, player1.x, player1.y);
            player1.x += Math.cos(angle) * 25;
            player1.y += Math.sin(angle) * 25;
        }
        
        // Magnetized attraction
        if (player1.magnetized || player2.magnetized) {
            const midX = (player1.x + player2.x) / 2;
            const midY = (player1.y + player2.y) / 2;
            
            player1.x = midX - 15;
            player1.y = midY;
            player2.x = midX + 15;
            player2.y = midY;
        }
    }

    /**
     * Handle token collision (win condition)
     */
    handleTokenCollision(player, token) {
        if (this.gamePhase !== 'racing') return;
        
        console.log(`[RaceScene] Player ${player.index + 1} won the race!`);
        
        // Check for magnetized players (they have priority)
        const magnetizedPlayers = this.players.filter(p => p.magnetized);
        const winner = magnetizedPlayers.length > 0 ? magnetizedPlayers[0] : player;
        
        // Add big points bonus for winning the race
        this.addPlayerPoints(winner.index, 100);
        
        this.endRace(winner);
    }
    
    /**
     * Handle booster collision
     */
    handleBoosterCollision(player, booster) {
        console.log(`[RaceScene] Player ${player.index + 1} collected booster`);
        
        // Apply booster effect using player method
        const duration = 4000 + this.rng.random() * 3000;
        player.applySpeedBoost(booster.speedMultiplier || 1.5, duration);
        
        // Add points for collecting booster
        this.addPlayerPoints(player.index, 10);
        
        // Remove booster
        booster.destroy();
        this.boosters = this.boosters.filter(b => b !== booster);
    }
    
    /**
     * Handle skill collision
     */
    handleSkillCollision(player, skill) {
        console.log(`[RaceScene] Player ${player.index + 1} collected skill: ${skill.skillType}`);
        
        this.activateSkill(player, skill.skillType);
        
        // Add points for collecting skill
        this.addPlayerPoints(player.index, 20);
        
        // Remove skill
        skill.destroy();
        this.skills = this.skills.filter(s => s !== skill);
    }
    
    /**
     * Handle obstacle collision
     */
    handleObstacleCollision(player, obstacle) {
        // Apply obstacle effect to player
        obstacle.applyEffect(player);
        
        // Some obstacles are destroyed on impact
        if (obstacle.obstacleType.destroyOnImpact) {
            obstacle.destroy();
            this.obstacles = this.obstacles.filter(o => o !== obstacle);
        }
    }

    /**
     * Handle LARGE barrier collision - DRAMATIC impact!
     */
    handleBarrierCollision(player, barrier) {
        // Only handle collisions for active barriers
        if (barrier.state !== 'active') return;
        
        // Delegate to the barrier's collision handler for proper effects
        barrier.handlePlayerCollision(player);
        
        console.log(`[RaceScene] Player ${player.index} hit LARGE ${barrier.barrierType.name} barrier!`);
    }

    /**
     * Activate a skill effect
     * @param {Object} player - Player who activated skill
     * @param {string} skillType - Type of skill
     */
    activateSkill(player, skillType) {
        switch (skillType) {
            case 'thunder':
                // Paralyze 3 random opponents
                const targets = this.players.filter(p => p !== player && p.active);
                for (let i = 0; i < Math.min(3, targets.length); i++) {
                    const target = targets[Math.floor(this.rng.random() * targets.length)];
                    if (!target.paralyzed) {
                        target.paralyzed = true;
                        target.setTint(0xffff00);
                        
                        this.managedSetTimeout(() => {
                            target.paralyzed = false;
                            target.clearTint();
                        }, 3000);
                    }
                }
                break;
                
            case 'fire':
                // Slow down 2 random opponents
                const burnTargets = this.players.filter(p => p !== player && p.active)
                    .sort(() => this.rng.random() - 0.5)
                    .slice(0, 2);
                    
                burnTargets.forEach(target => {
                    target.burning = true;
                    const originalSpeed = target.baseSpeed;
                    target.baseSpeed *= 0.5;
                    target.setTint(0xff4500);
                    
                    this.managedSetTimeout(() => {
                        target.burning = false;
                        target.baseSpeed = originalSpeed;  // Restore original speed
                        target.clearTint();
                    }, 5000);
                });
                break;
                
            case 'bubble':
                // Protect player from collisions
                player.hasBubble = true;
                const bubble = this.add.circle(player.x, player.y, 30, 0x00bfff, 0.3);
                
                // Follow player
                const bubbleUpdate = this.time.addEvent({
                    delay: 16,
                    callback: () => {
                        if (bubble && player && player.active) {
                            bubble.x = player.x;
                            bubble.y = player.y;
                        }
                    },
                    repeat: 500
                });
                
                this.managedSetTimeout(() => {
                    player.hasBubble = false;
                    bubble.destroy();
                    bubbleUpdate.remove();
                }, 8000);
                break;
                
            case 'magnet':
                // Attract to nearby racers (win condition if M token found)
                player.magnetized = true;
                player.setTint(0xff1493);
                
                this.managedSetTimeout(() => {
                    player.magnetized = false;
                    player.clearTint();
                }, 5000);
                break;
                
            case 'teleport':
                // Teleport all players randomly
                this.players.forEach(p => {
                    if (p.active) {
                        const newPos = this.getRandomValidPosition();
                        p.x = newPos.x;
                        p.y = newPos.y;
                        p.stuckCounter = 0;
                        
                        // Visual effect
                        const effect = this.add.circle(newPos.x, newPos.y, 40, 0x9400d3, 0.5);
                        this.managedCreateTween({
                            targets: effect,
                            scale: 0,
                            alpha: 0,
                            duration: 500,
                            onComplete: () => effect.destroy()
                        });
                    }
                });
                break;
        }
    }

    /**
     * Check player-to-player collisions
     */
    // checkPlayerCollisions method removed

    // handlePlayerCollision method removed

    /**
     * Update game timer and check time conditions
     */
    updateGameTimer() {
        if (this.gamePhase === 'racing') {
            this.gameTime--;
            
            const minutes = Math.floor(this.gameTime / 60);
            const seconds = this.gameTime % 60;
            const timeString = `TIME: ${minutes}:${seconds.toString().padStart(2, '0')}`;
            
            // Debug timer state
            if (this.timerText) {
                this.timerText.setText(timeString);
                
                // Log timer properties every 10 seconds for debugging
                if (this.gameTime % 10 === 0) {
                    console.log(`[RaceScene] Timer Debug:`, {
                        text: timeString,
                        x: this.timerText.x,
                        y: this.timerText.y,
                        visible: this.timerText.visible,
                        active: this.timerText.active,
                        depth: this.timerText.depth,
                        alpha: this.timerText.alpha,
                        scale: this.timerText.scaleX,
                        camera: {
                            x: this.cameras.main.scrollX,
                            y: this.cameras.main.scrollY,
                            zoom: this.cameras.main.zoom
                        }
                    });
                }
            } else {
                console.error(`[RaceScene] Timer text is null! Time: ${timeString}`);
            }
            
            // Flash timer when time is running low
            if (this.gameTime <= 30) {
                // Flash red when very low on time
                this.timerText.setColor(this.gameTime % 2 === 0 ? '#ff0000' : '#ffff00');
                this.timerText.setScale(1 + Math.sin(this.time.now * 0.01) * 0.05); // Pulsing effect
            } else if (this.gameTime <= 60) {
                // Orange warning when under 1 minute
                this.timerText.setColor('#ff8800');
            }
            
            if (this.gameTime <= 0) {
                this.endRace(); // Time up, no winner
            }
        }
    }

    /**
     * Maybe spawn a booster with dynamic chance
     */
    maybeSpawnBooster(spawnMultiplier = 1) {
        // Calculate chance based on multiplier (higher multiplier = more likely to spawn)
        const baseChance = 0.7; // 70% base chance
        const adjustedChance = Math.min(0.95, baseChance * spawnMultiplier); // Cap at 95%
        
        if (this.gamePhase !== 'racing' || this.rng.random() > adjustedChance) return;
        
        const position = this.getRandomValidPosition();
        const boosterTypes = [
            { name: 'banana', sprite: 'booster_banana', speedMultiplier: 1.5, color: 0xffffff },
            { name: 'toilet_paper', sprite: 'booster_toilet_paper', speedMultiplier: 2.0, color: 0xffffff },
            { name: 'poo', sprite: 'booster_poo', speedMultiplier: 0.7, color: 0xffffff },
            { name: 'king_kong', sprite: 'booster_king_kong', speedMultiplier: 1.8, color: 0xffffff },
            { name: 'antenna', sprite: 'booster_antenna', speedMultiplier: 1.3, color: 0xffffff }
        ];
        
        const boosterType = boosterTypes[Math.floor(this.rng.random() * boosterTypes.length)];
        
        // Create booster with fallback texture if needed
        if (!this.textures.exists(boosterType.sprite)) {
            // Create simple colored circle as fallback
            const graphics = this.add.graphics();
            graphics.fillStyle(boosterType.color || 0x00ff00, 1);
            graphics.fillCircle(16, 16, 15);
            graphics.generateTexture(boosterType.sprite, 32, 32);
            graphics.destroy();
            console.log(`[RaceScene] Created fallback texture for ${boosterType.name} booster`);
        }
        
        const booster = this.physics.add.sprite(position.x, position.y, boosterType.sprite);
        booster.setScale(0.6);
        booster.setTint(boosterType.color);
        booster.speedMultiplier = boosterType.speedMultiplier;
        booster.setDepth(10); // Ensure booster appears above track
        
        // Set physics body size for proper collision detection
        booster.body.setSize(32, 32);
        booster.body.setOffset(0, 0);
        
        // Glowing effect
        this.managedCreateTween({
            targets: booster,
            alpha: 0.5,
            duration: 500,
            yoyo: true,
            repeat: -1
        });
        
        this.boosters.push(booster);
        
        // Auto-remove after 10 seconds using managed timeout
        this.managedSetTimeout(() => {
            if (booster && booster.active) {
                booster.destroy();
                this.boosters = this.boosters.filter(b => b !== booster);
            }
        }, 10000);
    }

    /**
     * Maybe spawn a skill with dynamic chance
     */
    maybeSpawnSkill(spawnMultiplier = 1) {
        // Calculate chance based on multiplier (higher multiplier = more likely to spawn)
        const baseChance = 0.8; // 80% base chance
        const adjustedChance = Math.min(0.95, baseChance * spawnMultiplier); // Cap at 95%
        
        if (this.gamePhase !== 'racing' || this.rng.random() > adjustedChance) return;
        
        const position = this.getRandomValidPosition();
        const skillTypes = [
            { name: 'thunder', color: 0xffff00 },
            { name: 'fire', color: 0xff4500 },
            { name: 'bubble', color: 0x87ceeb },
            { name: 'magnet', color: 0xff1493 },
            { name: 'teleport', color: 0x9400d3 }
        ];
        const skillType = skillTypes[Math.floor(this.rng.random() * skillTypes.length)];
        const skillKey = `skill_${skillType.name}`;
        
        // Create skill with fallback texture if needed
        if (!this.textures.exists(skillKey)) {
            // Create star shape as fallback
            const graphics = this.add.graphics();
            graphics.fillStyle(skillType.color, 1);
            graphics.beginPath();
            // Draw star
            for (let i = 0; i < 10; i++) {
                const angle = (Math.PI * 2 * i) / 10;
                const radius = i % 2 === 0 ? 16 : 8;
                const x = 16 + Math.cos(angle - Math.PI / 2) * radius;
                const y = 16 + Math.sin(angle - Math.PI / 2) * radius;
                if (i === 0) {
                    graphics.moveTo(x, y);
                } else {
                    graphics.lineTo(x, y);
                }
            }
            graphics.closePath();
            graphics.fillPath();
            graphics.generateTexture(skillKey, 32, 32);
            graphics.destroy();
            console.log(`[RaceScene] Created fallback texture for ${skillType.name} skill`);
        }
        
        const skill = this.physics.add.sprite(position.x, position.y - 200, skillKey);
        skill.setScale(0.6);
        skill.skillType = skillType.name;
        skill.setVelocityY(100);
        skill.setDepth(10); // Ensure skill appears above track
        
        this.skills.push(skill);
        
        // Auto-remove after 8 seconds using managed timeout
        this.managedSetTimeout(() => {
            if (skill && skill.active) {
                skill.destroy();
                this.skills = this.skills.filter(s => s !== skill);
            }
        }, 8000);
    }
    
    /**
     * Maybe spawn obstacle based on spawn chance
     */
    maybeSpawnObstacle(spawnMultiplier = 1) {
        // Only spawn if not too many obstacles already
        if (this.obstacles.length >= 10) return;
        
        // Calculate chance based on multiplier and game time
        const baseChance = 0.95; // 95% base chance for better visibility
        const adjustedChance = Math.min(0.98, baseChance * spawnMultiplier);
        
        if (this.gamePhase !== 'racing' || Math.random() > adjustedChance) return;
        
        // Get appropriate obstacle types based on game time
        let availableTypes = [];
        const timeRemaining = this.gameTime;
        
        if (timeRemaining > 180) {
            // Early game: mostly static obstacles
            availableTypes = [
                OBSTACLE_TYPES.OIL_SLICK,
                OBSTACLE_TYPES.MUD_PATCH,
                OBSTACLE_TYPES.ROCK_BARRIER,
                OBSTACLE_TYPES.ICE_PATCH
            ];
        } else if (timeRemaining > 120) {
            // Mid game: mix of static and dynamic
            availableTypes = [
                OBSTACLE_TYPES.OIL_SLICK,
                OBSTACLE_TYPES.MUD_PATCH,
                OBSTACLE_TYPES.SPIKE_STRIP,
                OBSTACLE_TYPES.MOVING_BARRIER,
                OBSTACLE_TYPES.ICE_PATCH
            ];
        } else {
            // Late game: all obstacle types
            availableTypes = Object.values(OBSTACLE_TYPES);
        }
        
        // Select random obstacle type based on spawn weights
        const selectedType = this.selectWeightedObstacle(availableTypes);
        
        // Find position away from players
        const position = this.getRandomValidPosition();
        if (!position) return;
        
        // Check not too close to players
        let tooClose = false;
        for (const player of this.players) {
            const distance = Phaser.Math.Distance.Between(player.x, player.y, position.x, position.y);
            if (distance < 150) {
                tooClose = true;
                break;
            }
        }
        if (tooClose) return;
        
        // Create obstacle
        const obstacle = new Obstacle(this, position.x, position.y, selectedType);
        this.obstacles.push(obstacle);
        
        console.log(`[RaceScene] Spawned obstacle: ${selectedType.name} at (${Math.round(position.x)}, ${Math.round(position.y)})`);
    }
    
    /**
     * Select obstacle based on spawn weights
     */
    selectWeightedObstacle(availableTypes) {
        const totalWeight = availableTypes.reduce((sum, type) => sum + (type.spawnWeight || 0.1), 0);
        let random = Math.random() * totalWeight;
        
        for (const type of availableTypes) {
            random -= (type.spawnWeight || 0.1);
            if (random <= 0) {
                return type;
            }
        }
        
        return availableTypes[0]; // Fallback
    }

    /**
     * Maybe spawn LARGE temporary barrier - VERY BIG and dramatic!
     */
    maybeSpawnBarrier(spawnMultiplier = 1) {
        // Limit barriers - only allow 2 at once for dramatic effect
        if (this.barriers.length >= 2) return;
        
        // Lower spawn chance than obstacles - barriers are SPECIAL events
        const baseChance = 0.3; // Only 30% base chance - rare but impactful
        const adjustedChance = Math.min(0.6, baseChance * spawnMultiplier);
        
        if (this.gamePhase !== 'racing' || Math.random() > adjustedChance) return;
        
        // Get all barrier types
        const availableBarriers = Object.values(BARRIER_TYPES);
        const selectedType = availableBarriers[Math.floor(Math.random() * availableBarriers.length)];
        
        // Generate position - must be clear of players and other barriers
        let position = null;
        let attempts = 0;
        const maxAttempts = 20;
        
        while (!position && attempts < maxAttempts) {
            const testX = 200 + Math.random() * (this.trackWidth - 400);
            const testY = 200 + Math.random() * (this.trackHeight - 400);
            
            // Check distance from all players
            let tooClose = false;
            for (const player of this.players) {
                if (!player || !player.active) continue;
                const distance = Phaser.Math.Distance.Between(testX, testY, player.x, player.y);
                if (distance < 250) { // Larger clearance for big barriers
                    tooClose = true;
                    break;
                }
            }
            
            // Check distance from other barriers
            if (!tooClose) {
                for (const barrier of this.barriers) {
                    if (!barrier || !barrier.active) continue;
                    const distance = Phaser.Math.Distance.Between(testX, testY, barrier.x, barrier.y);
                    if (distance < 400) { // Big barriers need lots of space
                        tooClose = true;
                        break;
                    }
                }
            }
            
            if (!tooClose) {
                position = { x: testX, y: testY };
            }
            
            attempts++;
        }
        
        if (!position) {
            console.log('[RaceScene] Could not find safe position for LARGE barrier');
            return;
        }
        
        // Create LARGE barrier with warning system
        const barrier = new TemporaryBarrier(this, position.x, position.y, selectedType);
        this.barriers.push(barrier);
        
        console.log(`[RaceScene] Spawning LARGE ${selectedType.name} barrier at (${Math.round(position.x)}, ${Math.round(position.y)}) - ${selectedType.width}x${selectedType.height}!`);
    }

    /**
     * Update various game systems
     * @param {number} time - Current time
     */
    updateGameSystems(time) {
        // Update player position tracking for stuck detection
        this.players.forEach(player => {
            if (player.lastPositions && player.lastPositions.length >= 10) {
                const oldPos = player.lastPositions[0];
                const distanceMoved = Phaser.Math.Distance.Between(
                    player.x, player.y, oldPos.x, oldPos.y
                );
                
                if (distanceMoved < 5) {
                    this.handleStuckPlayer(player);
                }
            }
        });
    }
    
    /**
     * Update dynamic spawning system based on timer
     */
    updateDynamicSpawning(time) {
        if (this.gamePhase !== 'racing') return;
        
        // Calculate spawn multiplier based on remaining time
        let spawnMultiplier = 1;
        if (this.gameTime <= 60) {
            spawnMultiplier = 4; // 4x at 1 minute
        } else if (this.gameTime <= 120) {
            spawnMultiplier = 2; // 2x at 2 minutes
        }
        
        // Calculate dynamic intervals (shorter intervals = more frequent spawning)
        const boosterInterval = this.baseBoosterInterval / spawnMultiplier;
        const skillInterval = this.baseSkillInterval / spawnMultiplier;
        const obstacleInterval = this.baseObstacleInterval / spawnMultiplier;
        const barrierInterval = this.baseBarrierInterval / spawnMultiplier;
        
        // Spawn boosters
        if (time - this.lastBoosterSpawn >= boosterInterval) {
            this.maybeSpawnBooster(spawnMultiplier);
            this.lastBoosterSpawn = time;
        }
        
        // Spawn skills
        if (time - this.lastSkillSpawn >= skillInterval) {
            this.maybeSpawnSkill(spawnMultiplier);
            this.lastSkillSpawn = time;
        }
        
        // Spawn obstacles
        if (time - this.lastObstacleSpawn >= obstacleInterval) {
            this.maybeSpawnObstacle(spawnMultiplier);
            this.lastObstacleSpawn = time;
        }
        
        // Spawn LARGE barriers - VERY RARE but dramatic
        if (time - this.lastBarrierSpawn >= barrierInterval) {
            this.maybeSpawnBarrier(spawnMultiplier);
            this.lastBarrierSpawn = time;
        }
        
        // Debug logging when multiplier changes
        if (this.lastSpawnMultiplier !== spawnMultiplier) {
            console.log(`[RaceScene] Spawn rate changed: ${this.lastSpawnMultiplier || 1}x → ${spawnMultiplier}x (Time: ${this.gameTime}s)`);
            console.log(`[RaceScene] New intervals - Boosters: ${boosterInterval}ms, Skills: ${skillInterval}ms`);
            this.lastSpawnMultiplier = spawnMultiplier;
        }
    }

    /**
     * Handle stuck players
     * @param {Object} player - Stuck player
     */
    handleStuckPlayer(player) {
        console.log(`[RaceScene] Handling stuck player ${player.index + 1}`);
        
        // Try to find new direction first
        const newDirection = this.findBestDirection(player.x, player.y, player.moveDirection);
        player.moveDirection = newDirection;
        
        // If stuck for too long, teleport to nearby valid position
        if (player.stuckCounter > 120) {
            // Find nearest valid position instead of random
            const nearbyPos = this.findNearestValidPosition(player.x, player.y);
            
            if (nearbyPos) {
                // Add visual effect before teleport
                const flash = this.add.circle(player.x, player.y, 20, 0xffff00, 1);
                this.managedCreateTween({
                    targets: flash,
                    scale: 2,
                    alpha: 0,
                    duration: 300,
                    onComplete: () => flash.destroy()
                });
                
                // Teleport to nearby position
                player.x = nearbyPos.x;
                player.y = nearbyPos.y;
                player.stuckCounter = 0;
                
                console.log(`[RaceScene] Teleported stuck player ${player.index + 1} to nearby position`, nearbyPos);
            } else {
                // Fallback: aggressive direction change
                player.moveDirection += Math.PI * (0.5 + Math.random() * 0.5);
                player.stuckCounter = 60; // Reset partially
            }
        }
    }
    
    /**
     * Find nearest valid position to current position
     */
    findNearestValidPosition(x, y) {
        let nearestPos = null;
        let minDistance = Infinity;
        
        // Search in expanding circles
        for (let radius = 50; radius <= 200; radius += 50) {
            for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 8) {
                const testX = x + Math.cos(angle) * radius;
                const testY = y + Math.sin(angle) * radius;
                
                if (this.isPositionOnTrack(testX, testY)) {
                    const distance = Phaser.Math.Distance.Between(x, y, testX, testY);
                    if (distance < minDistance) {
                        minDistance = distance;
                        nearestPos = { x: testX, y: testY };
                    }
                }
            }
            
            // If we found a position at this radius, use it
            if (nearestPos) {
                return nearestPos;
            }
        }
        
        // Last resort: use any valid position
        return this.getRandomValidPosition();
    }

    /**
     * Find best direction for player movement
     * @param {number} x - Current X position  
     * @param {number} y - Current Y position
     * @param {number} currentDirection - Current movement direction
     * @returns {number} Best direction angle
     */
    findBestDirection(x, y, currentDirection) {
        const testDistance = 30;
        const angleStep = Math.PI / 8; // 22.5 degrees
        let bestAngle = currentDirection;
        let maxClearDistance = 0;
        
        // Test 16 directions
        for (let angle = 0; angle < Math.PI * 2; angle += angleStep) {
            let clearDistance = 0;
            
            // Check how far we can go in this direction
            for (let dist = 5; dist <= 60; dist += 5) {
                const testX = x + Math.cos(angle) * dist;
                const testY = y + Math.sin(angle) * dist;
                
                if (this.isPositionOnTrack(testX, testY)) {
                    clearDistance = dist;
                } else {
                    break;
                }
            }
            
            if (clearDistance > maxClearDistance) {
                maxClearDistance = clearDistance;
                bestAngle = angle;
            }
        }
        
        // If no good direction found, try opposite of current
        if (maxClearDistance < 10) {
            return currentDirection + Math.PI;
        }
        
        return bestAngle;
    }

    /**
     * Get random valid position on track
     * @returns {Object} Position {x, y}
     */
    getRandomValidPosition() {
        // Use track generator if available
        if (this.trackGenerator) {
            return this.trackGenerator.getRandomPosition();
        }
        
        // Fallback to simple random position
        const margin = 150;
        return {
            x: margin + Math.random() * (this.trackWidth - margin * 2),
            y: margin + Math.random() * (this.trackHeight - margin * 2)
        };
    }

    /**
     * Get farthest position from start
     * @param {Object} startPos - Starting position
     * @returns {Object} Farthest position
     */
    getFarthestPositionFromStart(startPos) {
        // Ensure minimum distance from start
        const MIN_DISTANCE = 500;  // Minimum 500 pixels away
        
        let farthestPos = null;
        let maxDistance = 0;
        
        for (const pos of this.validPositions) {
            const distance = Phaser.Math.Distance.Between(startPos.x, startPos.y, pos.x, pos.y);
            if (distance > maxDistance && distance > MIN_DISTANCE) {
                maxDistance = distance;
                farthestPos = pos;
            }
        }
        
        // If no position found far enough, create one at opposite side of screen
        if (!farthestPos) {
            console.warn('[RaceScene] No position found far enough, placing M token at opposite side');
            const margin = 150; // Keep away from edges for visibility
            
            // Place on opposite side of the screen from start position
            farthestPos = {
                x: startPos.x < this.trackWidth / 2 ? 
                    this.trackWidth - margin : margin,
                y: startPos.y < this.trackHeight / 2 ? 
                    this.trackHeight - margin : margin
            };
            
            // Add some randomness to avoid always being in exact corner
            farthestPos.x += (Math.random() - 0.5) * 200;
            farthestPos.y += (Math.random() - 0.5) * 200;
            
            // Ensure within bounds
            farthestPos.x = Math.max(margin, Math.min(this.trackWidth - margin, farthestPos.x));
            farthestPos.y = Math.max(margin, Math.min(this.trackHeight - margin, farthestPos.y));
        }
        
        console.log(`[RaceScene] M token distance from start: ${maxDistance.toFixed(0)} pixels`);
        return farthestPos;
    }

    /**
     * Check if there's space around position for multiple players
     * @param {Object} position - Center position
     * @param {number} playerCount - Number of players
     * @returns {boolean} Has enough space
     */
    checkSpaceForPlayers(position, playerCount) {
        // Check grid pattern around position (using 2x3 grid with 40 pixel spacing)
        for (let i = 0; i < playerCount; i++) {
            const col = i % 2;  // 2 columns
            const row = Math.floor(i / 2);  // 3 rows
            const offsetX = (col - 0.5) * 40;
            const offsetY = (row - 1) * 40;
            
            const testX = position.x + offsetX;
            const testY = position.y + offsetY;
            
            // Check with margin for player size (48x48 sprite)
            const margin = 24;
            if (!this.isPositionOnTrack(testX, testY) || 
                !this.isPositionOnTrack(testX - margin, testY) ||
                !this.isPositionOnTrack(testX + margin, testY) ||
                !this.isPositionOnTrack(testX, testY - margin) ||
                !this.isPositionOnTrack(testX, testY + margin)) {
                return false;
            }
        }
        
        return true;
    }

    /**
     * Show start position indicator
     * @param {Object} position - Start position
     */
    showStartIndicator(position) {
        const indicator = this.add.circle(position.x, position.y, 50, 0x00ff00, 0.5);
        const text = this.add.text(position.x, position.y, 'START', {
            fontSize: '20px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        
        this.managedCreateTween({
            targets: [indicator, text],
            alpha: 0,
            scale: 2,
            duration: 3000,
            onComplete: () => {
                indicator.destroy();
                text.destroy();
            }
        });
    }


    /**
     * Show race results
     * @param {Object} winner - Winning player
     */
    showRaceResults(winner) {
        let resultText = 'TIME UP! NO WINNER!';
        let resultColor = '#ff0000';
        
        if (winner) {
            const playerName = this.playerStats[winner.index]?.name || `Player ${winner.index + 1}`;
            resultText = `${playerName} WINS!`;
            resultColor = '#00ff00';
        }
        
        const resultsDisplay = this.add.text(960, 540, resultText, {
            fontSize: '72px',
            fontFamily: 'Courier New',
            color: resultColor,
            stroke: '#000000',
            strokeThickness: 6,
            fontWeight: 'bold'
        }).setOrigin(0.5).setDepth(1000); // Ensure text appears above track
        
        // Animate results using managed tween
        this.managedCreateTween({
            targets: resultsDisplay,
            scale: 1.2,
            duration: 500,
            yoyo: true,
            repeat: -1
        });
        
        // Process auto-bet results
        this.processAutoBetResults(winner);
        
        // Store for cleanup
        this.resultsDisplay = resultsDisplay;
    }

    /**
     * Process auto-bet results
     * @param {Object} winner - Winning player
     */
    processAutoBetResults(winner) {
        const autoBetAmount = 100;
        
        if (winner && winner.index === 0) { // Player 1 is always the current player
            // Player won
            const payout = 500; // Fixed payout for winning
            if (this.autoChargeText) {
                this.autoChargeText.setText(`You won ${payout} tokens!`);
            }
            console.log(`[RaceScene] Player won: ${payout} tokens`);
        } else {
            // Player lost
            if (this.autoChargeText) {
                this.autoChargeText.setText(`You lost ${autoBetAmount} tokens`);
            }
            console.log(`[RaceScene] Player lost: ${autoBetAmount} tokens`);
        }
    }

    /**
     * Update player statistics
     * @param {Object} winner - Winning player
     */
    updatePlayerStatistics(winner) {
        // Update race count for all players
        Object.values(this.playerStats).forEach(stats => {
            stats.totalRaces++;
        });
        
        // Update winner stats
        if (winner && this.playerStats[winner.index]) {
            this.playerStats[winner.index].wins++;
        }
        
        // Update leaderboard display - DISABLED per user request
        // this.updateLeaderboardDisplay();
    }

    // REMOVED: updateLeaderboardDisplay() - Disabled per user request

    /**
     * Reset for next race
     */
    resetForNextRace() {
        console.log('[RaceScene] Resetting for next race');
        
        this.gamePhase = 'resetting';
        this.raceNumber++;
        
        // Reset survival points timer
        this.lastSurvivalPointsTime = null;
        
        // Clean up results display
        if (this.resultsDisplay) {
            this.resultsDisplay.destroy();
            this.resultsDisplay = null;
        }
        
        // Clear items first
        this.clearGameItems();
        
        // Regenerate map with new background for variety
        this.regenerateMap();
        
        // Reset players (must be done before spawning token)
        this.resetPlayers();
        
        // Respawn M token AFTER players are positioned
        this.spawnMToken();
        
        // Start new race directly using managed timeout
        this.managedSetTimeout(() => {
            this.showAutoChargeNotice();
            // Start race after notice
            this.managedSetTimeout(() => {
                this.startRacingPhase();
            }, 3000);
        }, 1000);
        
        // Update race number display
        const raceNumText = this.children.list.find(child => 
            child.type === 'Text' && child.text && child.text.includes('Race #')
        );
        if (raceNumText) {
            raceNumText.setText(`Race #${this.raceNumber}`);
        }
    }

    /**
     * Reset all players to new starting position
     */
    resetPlayers() {
        // Get new start position
        let newStartPosition = this.getRandomValidPosition();
        let attempts = 0;
        
        while (attempts < 50) {
            const hasSpace = this.checkSpaceForPlayers(newStartPosition, this.players.length);
            if (hasSpace) break;
            
            newStartPosition = this.getRandomValidPosition();
            attempts++;
        }
        
        // Reset each player
        this.players.forEach((player, i) => {
            if (!player) return;
            
            // Calculate new position (matching spawn spacing with 40 pixel separation)
            const col = i % 2;  // 2 columns
            const row = Math.floor(i / 2);  // 3 rows for 6 players
            const offsetX = (col - 0.5) * 40;
            const offsetY = (row - 1) * 40;
            
            player.x = newStartPosition.x + offsetX;
            player.y = newStartPosition.y + offsetY;
            
            // Reset player state
            player.currentSpeed = player.baseSpeed;
            player.speedMultiplier = 1;
            player.moveDirection = this.rng.random() * Math.PI * 2;
            player.stuckCounter = 0;
            player.lastPositions = [];
            
            // Reset spawn time for collision immunity
            player.spawnTime = Date.now();
            player.hasStartedMoving = false;
            
            // Set initial velocity for new position
            const initialSpeed = 120 + Math.random() * 60;
            const velocityX = Math.cos(player.moveDirection) * initialSpeed;
            const velocityY = Math.sin(player.moveDirection) * initialSpeed;
            player.body.setVelocity(velocityX, velocityY);
            player.initialVelocity = { x: velocityX, y: velocityY };
            
            // Clear status effects
            player.paralyzed = false;
            player.burning = false;
            player.hasBubble = false;
            player.magnetized = false;
            player.clearTint();
            
            // Scale is now handled properly by player reset
            // Removed problematic setScale(0.25) that was causing size bug
            
            // Update name text
            if (player.nameText) {
                player.nameText.x = player.x;
                player.nameText.y = player.y + 25;
            }
        });
        
        // Show new start indicator
        this.showStartIndicator(newStartPosition);
    }

    /**
     * Clear all game items
     */
    clearGameItems() {
        // Clear boosters
        this.boosters.forEach(booster => {
            if (booster && booster.active) {
                booster.destroy();
            }
        });
        this.boosters = [];
        
        // Clear skills
        this.skills.forEach(skill => {
            if (skill && skill.active) {
                skill.destroy();
            }
        });
        this.skills = [];
        
        // Clear obstacles
        this.obstacles.forEach(obstacle => {
            if (obstacle && obstacle.active) {
                obstacle.destroy();
            }
        });
        this.obstacles = [];
        
        // Clear LARGE barriers
        this.barriers.forEach(barrier => {
            if (barrier && barrier.active) {
                barrier.destroy();
            }
        });
        this.barriers = [];
        
        // Clear M token
        if (this.mToken && this.mToken.active) {
            this.mToken.destroy();
            this.mToken = null;
        }
        
        // Clear goal arrow
        if (this.goalArrow && this.goalArrow.active) {
            this.goalArrow.destroy();
            this.goalArrow = null;
        }
        
        console.log('[RaceScene] All game items cleared including M token');
    }


    /**
     * Set debug panel visibility
     * @param {boolean} visible - Panel visibility
     */
    setDebugPanelVisible(visible) {
        if (this.debugPanel) {
            this.debugPanel.background.setVisible(visible);
            this.debugPanel.title.setVisible(visible);
            this.debugPanel.contentText.setVisible(visible);
            if (this.debugPanel.playerText) {
                this.debugPanel.playerText.setVisible(visible);
            }
            if (this.debugPanel.bgText) {
                this.debugPanel.bgText.setVisible(visible);
            }
        }
    }

    /**
     * Update debug information - Enhanced for visual testing
     */
    updateDebugInfo() {
        if (!this.debugMode || !this.debugText) return;
        
        // Main debug info
        let debugInfo = `━━━ GAME STATE ━━━\n`;
        debugInfo += `Phase: ${this.gamePhase}\n`;
        debugInfo += `Time: ${this.gameTime}s\n`;
        debugInfo += `Players: ${this.players.length}\n`;
        debugInfo += `Boosters: ${this.boosters.length}\n`;
        debugInfo += `Skills: ${this.skills.length}\n`;
        debugInfo += `\n━━━ CAMERA ━━━\n`;
        debugInfo += `Position: (${this.cameras.main.scrollX.toFixed(0)}, ${this.cameras.main.scrollY.toFixed(0)})\n`;
        debugInfo += `Zoom: ${this.cameras.main.zoom}\n`;
        debugInfo += `Center: (${this.cameras.main.centerX}, ${this.cameras.main.centerY})\n`;
        
        this.debugText.setText(debugInfo);
        
        // Player details
        if (this.players.length > 0) {
            let playerInfo = `━━━ PLAYER SPRITES ━━━\n`;
            this.players.forEach((player, i) => {
                const velocity = player.body ? 
                    Math.sqrt(player.body.velocity.x ** 2 + player.body.velocity.y ** 2) : 0;
                
                playerInfo += `P${i + 1}: ${player.visible ? '✓' : '✗'} `;
                playerInfo += `Pos:(${Math.floor(player.x)},${Math.floor(player.y)}) `;
                playerInfo += `Vel:${velocity.toFixed(2)} `;
                playerInfo += `Tex:${player.texture?.key || 'NONE'}\n`;
                
                if (i === 0) {
                    playerInfo += `  Speed: ${player.currentSpeed?.toFixed(3) || 'N/A'} (base: ${player.baseSpeed?.toFixed(3) || 'N/A'})\n`;
                    playerInfo += `  Dir: ${player.moveDirection?.toFixed(2) || 'N/A'} rad\n`;
                    playerInfo += `  Stuck: ${player.stuckCounter || 0}\n`;
                    playerInfo += `  Size: ${player.displayWidth}x${player.displayHeight}\n`;
                }
            });
            
            if (this.playerDebugText) {
                this.playerDebugText.setText(playerInfo);
            }
        }
        
        // Background info
        let bgInfo = `━━━ BACKGROUNDS ━━━\n`;
        const backgrounds = this.children.list.filter(child => 
            child.texture && (
                child.texture.key.includes('bg') || 
                child.texture.key.includes('background') ||
                child.texture.key.includes('map') ||
                child.texture.key.includes('track')
            )
        );
        
        if (backgrounds.length > 0) {
            backgrounds.forEach((bg, i) => {
                const centered = Math.abs(bg.x - 960) < 10 && Math.abs(bg.y - 540) < 10;
                bgInfo += `BG${i + 1}: ${bg.texture.key}\n`;
                bgInfo += `  Pos: (${bg.x}, ${bg.y}) ${centered ? '✓ CENTERED' : '✗ OFF-CENTER'}\n`;
                bgInfo += `  Size: ${bg.width}x${bg.height}\n`;
            });
        } else {
            bgInfo += 'No backgrounds detected\n';
        }
        
        bgInfo += `\nExpected Center: (960, 540)\n`;
        bgInfo += `World Size: ${this.trackWidth}x${this.trackHeight}`;
        
        if (this.bgDebugText) {
            this.bgDebugText.setText(bgInfo);
        }
    }

    /**
     * Return to lobby/menu
     */
    returnToLobby() {
        console.log('[RaceScene] Returning to lobby');
        
        // Prevent multiple calls
        if (this.isLeavingScene) {
            console.log('[RaceScene] Already leaving scene, ignoring duplicate call');
            return;
        }
        this.isLeavingScene = true;
        
        // Clean up
        this.sound.stopAll();
        
        // Check if camera exists and is ready
        if (this.cameras && this.cameras.main) {
            // Fade out before transitioning to prevent blackout
            this.cameras.main.fade(250, 0, 0, 0);
            
            // Add timeout fallback for camera fade
            const fadeTimeout = this.time.delayedCall(500, () => {
                console.warn('[RaceScene] Camera fade timeout, forcing transition');
                this.completeLobbyTransition();
            });
            
            this.cameras.main.once('camerafadeoutcomplete', () => {
                if (fadeTimeout) {
                    fadeTimeout.destroy();
                }
                this.completeLobbyTransition();
            });
        } else {
            // Fallback if camera isn't ready
            console.warn('[RaceScene] Camera not ready, using direct transition');
            this.completeLobbyTransition();
        }
    }
    
    /**
     * Complete the lobby transition (extracted to prevent duplication)
     */
    completeLobbyTransition() {
        // Reset leaving flag
        this.isLeavingScene = false;
        
        // Ensure camera is reset before transition
        if (this.cameras && this.cameras.main) {
            this.cameras.main.setAlpha(1);
            this.cameras.main.clearFX();
        }
        
        // Return to appropriate scene
        if (this.gameData.isQuickPlay) {
            console.log('[RaceScene] Transitioning to MainMenuScene');
            this.scene.start('MainMenuScene');
            // Ensure the next scene's camera starts visible
            const nextScene = this.scene.get('MainMenuScene');
            if (nextScene && nextScene.cameras && nextScene.cameras.main) {
                nextScene.cameras.main.fadeIn(0);
            }
        } else {
            console.log('[RaceScene] Transitioning to LobbyScene');
            this.scene.start('LobbyScene', {
                roomData: this.gameData,
                isHost: this.gameData.isHost || false
            });
            // Ensure the next scene's camera starts visible
            const nextScene = this.scene.get('LobbyScene');
            if (nextScene && nextScene.cameras && nextScene.cameras.main) {
                nextScene.cameras.main.fadeIn(0);
            }
        }
    }

    /**
     * Scene cleanup
     */
    /**
     * Override redirect to auth - go to main menu instead of login on auth issues
     */
    redirectToAuth() {
        console.log('[RaceScene] Auth issue detected, redirecting to main menu instead of login');
        this.scene.start('MainMenuScene');
    }

    /**
     * Handle authentication context ready (override from AuthenticatedScene)
     */
    onAuthContextReady(userContext, isAuthenticated) {
        console.log('[RaceScene] Auth context ready:', { isAuthenticated, username: userContext?.username });
        // Update player identification in race
        this.updatePlayerIdentification();
    }
    
    /**
     * Handle user context updates (override from AuthenticatedScene)
     */
    onUserContextUpdate(userData) {
        console.log('[RaceScene] User context updated:', userData?.username);
        // Update player display when user data changes
        this.updatePlayerIdentification();
    }
    
    /**
     * Handle auth state changes (override from AuthenticatedScene)
     */
    onAuthStateChange(stateChange) {
        console.log('[RaceScene] Auth state changed:', stateChange.type);
        if (stateChange.type === 'logout' || stateChange.type === 'session_expired') {
            // Return to main menu on logout
            this.scene.start('MainMenuScene');
        }
    }
    
    /**
     * Update player identification with authenticated user data
     */
    updatePlayerIdentification() {
        const username = this.getUsername() || 'Guest';
        const userId = this.getUserId();
        
        // Update player data if available
        if (this.players && this.players.length > 0) {
            // Find the current player and update their identification
            const currentPlayer = this.players.find(p => p.isCurrentPlayer);
            if (currentPlayer) {
                currentPlayer.username = username;
                currentPlayer.userId = userId;
            }
        }
    }

    /**
     * Show play again dialog to ask user if they want another race
     */
    showPlayAgainDialog() {
        console.log('[RaceScene] Showing play again dialog');

        // Create semi-transparent overlay
        const overlay = this.add.rectangle(960, 540, 1920, 1080, 0x000000, 0.7)
            .setDepth(2000)
            .setInteractive();

        // Create dialog panel
        const dialogPanel = this.add.rectangle(960, 540, 600, 300, 0x222222, 1)
            .setDepth(2001)
            .setStrokeStyle(4, 0x00ff00);

        // Add "Play Again?" text
        const titleText = this.add.text(960, 480, 'PLAY AGAIN?', {
            fontSize: '48px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            fontWeight: 'bold'
        }).setOrigin(0.5).setDepth(2002);

        // Create Yes button
        const yesButton = this.add.rectangle(860, 580, 120, 50, 0x00ff00, 1)
            .setDepth(2002)
            .setStrokeStyle(2, 0xffffff)
            .setInteractive({ useHandCursor: true });

        const yesText = this.add.text(860, 580, 'YES', {
            fontSize: '24px',
            fontFamily: 'Courier New',
            color: '#000000',
            fontWeight: 'bold'
        }).setOrigin(0.5).setDepth(2003);

        // Create No button  
        const noButton = this.add.rectangle(1060, 580, 120, 50, 0xff0000, 1)
            .setDepth(2002)
            .setStrokeStyle(2, 0xffffff)
            .setInteractive({ useHandCursor: true });

        const noText = this.add.text(1060, 580, 'NO', {
            fontSize: '24px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            fontWeight: 'bold'
        }).setOrigin(0.5).setDepth(2003);

        // Button hover effects
        yesButton.on('pointerover', () => {
            yesButton.setScale(1.1);
            yesText.setScale(1.1);
        });
        yesButton.on('pointerout', () => {
            yesButton.setScale(1);
            yesText.setScale(1);
        });

        noButton.on('pointerover', () => {
            noButton.setScale(1.1);
            noText.setScale(1.1);
        });
        noButton.on('pointerout', () => {
            noButton.setScale(1);
            noText.setScale(1);
        });

        // Yes button click handler
        yesButton.on('pointerdown', () => {
            console.log('[RaceScene] User chose to play again');
            
            // Clean up dialog
            overlay.destroy();
            dialogPanel.destroy();
            titleText.destroy();
            yesButton.destroy();
            yesText.destroy();
            noButton.destroy();
            noText.destroy();
            
            // Start another race
            this.resetForNextRace();
        });

        // No button click handler
        noButton.on('pointerdown', () => {
            console.log('[RaceScene] User chose to exit');
            
            // Clean up dialog
            overlay.destroy();
            dialogPanel.destroy();
            titleText.destroy();
            yesButton.destroy();
            yesText.destroy();
            noButton.destroy();
            noText.destroy();
            
            // Return to main menu
            this.returnToLobby();
        });

        // Store dialog elements for potential cleanup
        this.playAgainDialog = {
            overlay,
            dialogPanel,
            titleText,
            yesButton,
            yesText,
            noButton,
            noText
        };
    }
    
    /**
     * Scene cleanup (enhanced with AuthenticatedScene cleanup)
     */
    destroy() {
        console.log('[RaceScene] Starting scene destruction with memory cleanup');
        
        // Stop all sounds
        this.sound.stopAll();
        
        // Clean up memory-managed resources
        if (this.memoryManager) {
            this.memoryManager.destroy();
        }
        
        // Clean up timers and tweens (fallback for any unmanaged resources)
        this.tweens.killAll();
        this.time.removeAllEvents();
        
        console.log('[RaceScene] Scene destroyed with memory cleanup');
        // Call parent destroy for auth cleanup
        super.destroy();
    }
}